import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { formatDate, formatRelativeTime } from '@utils/format';
import { ActiveBadge } from '@features/tags/components/cells/ActiveBadge';
import { ApmcLocationCell } from './components/cells/ApmcLocationCell';
import { ApmcRowActions } from './components/cells/ApmcRowActions';
import type { Apmc } from './schema';

export interface ApmcRowActionHandlers {
  onEdit: (row: Apmc) => void;
}

export function buildApmcsColumns(
  actions: ApmcRowActionHandlers,
): ColDef<Apmc>[] {
  return [
    {
      colId: 'name',
      headerName: 'APMC',
      flex: 1.5,
      minWidth: 220,
      sortable: true,
      pinned: 'left',
      lockPinned: true,
      cellRenderer: ApmcLocationCell,
      valueGetter: (p) => p.data?.name ?? '',
    },
    {
      colId: 'code',
      field: 'code',
      headerName: 'Code',
      width: 140,
      sortable: true,
      cellRenderer: (p: ICellRendererParams<Apmc>) =>
        p.data ? (
          <code className="rounded bg-surface-muted px-1.5 py-0.5 font-mono text-[12.5px] text-foreground">
            {p.data.code}
          </code>
        ) : null,
    },
    {
      colId: 'district',
      field: 'districtName',
      headerName: 'District',
      flex: 1,
      minWidth: 140,
      sortable: true,
    },
    {
      colId: 'state',
      field: 'stateName',
      headerName: 'State',
      flex: 1,
      minWidth: 140,
      sortable: true,
    },
    {
      field: 'isActive',
      headerName: 'Active',
      width: 110,
      cellRenderer: ActiveBadge,
    },
    {
      colId: 'updated',
      field: 'updatedAt',
      headerName: 'Updated',
      width: 200,
      sortable: true,
      cellRenderer: (p: ICellRendererParams<Apmc>) =>
        p.data ? (
          <div className="flex flex-col leading-tight">
            <span className="text-[13px] font-semibold text-foreground">
              {formatDate(p.data.updatedAt)}
            </span>
            <span className="mt-0.5 text-[11px] text-foreground-muted">
              {formatRelativeTime(p.data.updatedAt)}
            </span>
          </div>
        ) : null,
    },
    {
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filter: false,
      pinned: 'right',
      cellRenderer: (p: ICellRendererParams<Apmc>) =>
        p.data ? <ApmcRowActions row={p.data} {...actions} /> : null,
    },
  ];
}
